import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { logError } from "@/lib/logger";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import CandidateDrawer from "@/components/CandidateDrawer";
import PersonDetailPanel from "@/components/PersonDetailPanel";
import AddedByChip from "@/components/AddedByChip";
import BulkReachOutModal from "@/components/BulkReachOutModal";
import BulkStaleCvNudgeModal from "@/components/BulkStaleCvNudgeModal";
import ScheduleInterviewModal from "@/components/ScheduleInterviewModal";
import SkillsChipInput from "@/components/SkillsChipInput";
import CvFreshnessBadge from "@/components/CvFreshnessBadge";
import CvActions from "@/components/CvActions";
import { useChatDensity } from "@/lib/useChatDensity";
import VerifiedTierChip from "@/components/VerifiedTierChip";
import { IN_CITIES } from "@/lib/indiaGeo";
import {
    MagnifyingGlass,
    Sparkle,
    Globe,
    UserPlus,
    Upload,
    User,
    Crown,
    DotsThreeVertical,
    ChatCircleDots,
    FunnelSimple,
    X,
    Tag,
    Copy,
    DownloadSimple,
    CheckSquare,
    Square,
    PaperPlaneTilt,
    CalendarBlank,
    LinkSimple,
    Bell,
    MapPin,
    Briefcase,
    Lightning,
    Rows,
    List as ListIcon,
    SealCheck,
    ArrowRight,
    FileText,
} from "@phosphor-icons/react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from "@/components/ui/sheet";

const STAGES = ["Applied", "Screening", "Interview", "Offer", "Hired", "Rejected"];
const SOURCES = [
    { value: "self_apply", label: "Self-apply" },
    { value: "public", label: "Public link" },
    { value: "manual", label: "Tagged" },
    { value: "csv", label: "CSV" },
    { value: "referral", label: "Referral" },
    { value: "linkedin", label: "LinkedIn" },
    { value: "job_board", label: "Job board" },
    { value: "direct", label: "Direct" },
];
const ADDED_WITHIN = [
    { value: "", label: "Any time" },
    { value: "7d", label: "Last 7 days" },
    { value: "30d", label: "Last 30 days" },
    { value: "90d", label: "Last 90 days" },
];
const SORTS = [
    { value: "recent", label: "Most recent" },
    { value: "experience", label: "Experience" },
    { value: "name", label: "Name (A–Z)" },
];

const SOURCE_META = {
    self_apply: { label: "Self-apply", icon: User, cls: "text-cobalt" },
    public: { label: "Public link", icon: Globe, cls: "text-[#059669]" },
    manual: { label: "Tagged", icon: UserPlus, cls: "text-[#7E22CE]" },
    csv: { label: "CSV", icon: Upload, cls: "text-[#A3A3A3]" },
    daddytalk: { label: "DaddyTalk", icon: ChatCircleDots, cls: "text-cobalt" },
};

function SourceBadge({ source }) {
    const meta = SOURCE_META[source] || SOURCE_META.manual;
    const Icon = meta.icon;
    return (
        <span
            data-testid={`list-source-${source}`}
            className={`inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider ${meta.cls}`}
        >
            <Icon size={10} weight="bold" /> {meta.label}
        </span>
    );
}

function FeaturedBadge({ compact = false }) {
    return (
        <span
            data-testid="candidate-featured-badge"
            title="Boosted candidate — plan-holder"
            className={`inline-flex items-center gap-1 bg-[#F59E0B] text-white font-black uppercase tracking-wider ${
                compact ? "text-[8px] px-1.5 py-0.5" : "text-[9px] px-2 py-0.5"
            }`}
        >
            <Crown size={compact ? 8 : 9} weight="fill" />
            {compact ? "" : "Boosted"}
        </span>
    );
}

// V1.9.35 — surface how the candidate entered the system.
// V2.0.3 — accepts optional `source` to sub-classify agency uploads
// (fast_track, referral) into their own chips.
function OriginChip({ origin, source, testId, size = "sm" }) {
    if (!origin) return null;
    const src = (source || "").toLowerCase();
    let key = origin;
    if (origin === "organic" && src === "referral") key = "referral";
    else if (origin === "agency_uploaded" && src === "fast_track") key = "fast_track";
    else if (origin === "agency_uploaded" && src === "daddytalk") key = "daddytalk";
    else if (origin === "daddytalk_fast_track") key = "fast_track";
    const map = {
        organic: { label: "Self-signup", color: "bg-emerald-100 text-emerald-800", title: "Candidate signed up directly via a public job link" },
        referral: { label: "Referral", color: "bg-emerald-100 text-emerald-800", title: "Came via a candidate referral link" },
        daddytalk: { label: "DaddyTalk", color: "bg-cobalt/10 text-cobalt", title: "Came in via a DaddyTalk chat with a recruiter" },
        fast_track: { label: "Fast-Track", color: "bg-cobalt/10 text-cobalt", title: "Converted from a DaddyTalk chat via Fast-Track" },
        agency_uploaded: { label: "Sourced", color: "bg-amber-100 text-amber-800", title: "Recruiter-provisioned (bulk import or manual add)" },
    };
    const cfg = map[key] || { label: key, color: "bg-ink/10 text-ink/70", title: key };
    const sizeCls = size === "md" ? "text-[10px] px-2 py-0.5" : "text-[9px] px-1.5 py-0.5";
    return (
        <span
            data-testid={testId}
            title={cfg.title}
            className={`inline-flex items-center gap-1 font-black uppercase tracking-wider rounded-sm ${sizeCls} ${cfg.color}`}
        >
            {cfg.label}
        </span>
    );
}

const STAGE_ORDER = ["Applied", "Screening", "Interview", "Offer", "Hired"];
function nextStageOf(cur) {
    const idx = STAGE_ORDER.indexOf(cur);
    if (idx < 0 || idx >= STAGE_ORDER.length - 1) return null;
    return STAGE_ORDER[idx + 1];
}

function initialsOf(name) {
    return (name || "")
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0])
        .join("")
        .toUpperCase() || "?";
}

// Compact circular avatar for the list rows — only rendered when the workspace
// admin has enabled candidate photos in list view. Falls back to initials.
// V2.0.3 — supports an optional `isOnline` green ring + a colored gradient
// background derived from the name so each candidate gets a consistent shade
// even without a photo.
function _hueForName(name) {
    let h = 0;
    for (const ch of String(name || "")) h = (h * 31 + ch.charCodeAt(0)) % 360;
    return h;
}

function RowAvatar({ url, name, size = 36, testid, isOnline = false, rounded = true }) {
    const [broken, setBroken] = useState(false);
    const hue = _hueForName(name);
    const bg = url && !broken
        ? undefined
        : `linear-gradient(135deg, hsl(${hue}, 65%, 55%), hsl(${(hue + 40) % 360}, 65%, 45%))`;
    const dot = Math.max(10, Math.round(size * 0.28));
    return (
        <div className="relative shrink-0" style={{ width: size, height: size }} data-testid={testid}>
            <div
                style={{ width: size, height: size, background: bg }}
                className={`${rounded ? "rounded-full" : ""} bg-cobalt text-white font-black flex items-center justify-center overflow-hidden text-xs`}
            >
                {url && !broken ? (
                    <img
                        src={url}
                        alt=""
                        onError={() => setBroken(true)}
                        className="w-full h-full object-cover"
                    />
                ) : (
                    <span>{initialsOf(name)}</span>
                )}
            </div>
            {isOnline && (
                <span
                    data-testid={`${testid}-online-dot`}
                    aria-label="Online now"
                    title="Online now"
                    style={{ width: dot, height: dot }}
                    className="absolute bottom-0 right-0 rounded-full bg-emerald-500 border-2 border-white shadow-sm"
                />
            )}
        </div>
    );
}

// V2.0.3 — Comfy mobile candidate row. Surfaces avatar w/ online dot,
// origin differentiator, stage, location, experience, work-mode, boost,
// verified, and any pending action (No CV / Verify pending / recency).
function _relTime(iso) {
    if (!iso) return null;
    try {
        const then = new Date(iso).getTime();
        const now = Date.now();
        const diff = Math.max(0, now - then);
        const min = Math.floor(diff / 60000);
        if (min < 1) return "just now";
        if (min < 60) return `${min}m ago`;
        const hr = Math.floor(min / 60);
        if (hr < 24) return `${hr}h ago`;
        const day = Math.floor(hr / 24);
        if (day < 30) return `${day}d ago`;
        return null;
    } catch { return null; }
}


// V2.0.33 (Feb 22, 2026) — Priority/Type/Signals filter chip rows were
// retired from the recruiter list (they duplicated the Superadmin
// Candidate Signals portal). The row-level pill visuals still exist
// (see DesktopPriorityPill + MobileCandidateRow) — the recruiter list
// now surfaces a single **Stage** chip row for daily triage chunking.
const STAGE_CHIP_META = {
    Applied:    { cls: "bg-white border border-ink/15 text-ink hover:bg-black/5",              active: "bg-ink text-white" },
    Screening:  { cls: "bg-cobalt/10 text-cobalt hover:bg-cobalt/20",                            active: "bg-cobalt text-white" },
    Interview:  { cls: "bg-purple-100 text-purple-800 hover:bg-purple-200",                     active: "bg-purple-500 text-white" },
    Offer:      { cls: "bg-emerald-100 text-emerald-800 hover:bg-emerald-200",                  active: "bg-emerald-600 text-white" },
    Hired:      { cls: "bg-emerald-500/15 text-emerald-800 hover:bg-emerald-500/25",            active: "bg-emerald-700 text-white" },
    Rejected:   { cls: "bg-red-100 text-red-800 hover:bg-red-200",                              active: "bg-red-600 text-white" },
};

function StageChipsRow({ stage, setStage, stageCounts, total }) {
    const totalRow = stageCounts && Object.values(stageCounts).reduce((a, b) => a + (b || 0), 0);
    // Fallback to server-reported total if by_stage buckets zero out due
    // to non-standard stage values on legacy candidates.
    const allCount = totalRow || total || 0;
    return (
        <div className="mb-4">
            <div className="text-[10px] font-black uppercase tracking-[0.2em] text-ink/45 mb-1.5 pl-1">
                Stage
            </div>
            <div className="flex flex-nowrap sm:flex-wrap gap-2 items-center overflow-x-auto sm:overflow-visible pb-1 sm:pb-0 -mx-1 px-1 no-scrollbar chip-scroller">
                <button
                    data-testid="filter-stage-all"
                    onClick={() => setStage("")}
                    className={`shrink-0 inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-[11px] font-black uppercase tracking-wider transition-colors ${
                        stage === "" ? "bg-ink text-white" : "border border-ink/10 text-ink/70 hover:bg-black/5 bg-white"
                    }`}
                >
                    All
                    <span className={`inline-flex items-center justify-center rounded-full text-[9px] px-1.5 min-w-[18px] h-[16px] ${stage === "" ? "bg-white/25" : "bg-black/5 text-ink/70"}`}>
                        {allCount}
                    </span>
                </button>
                {STAGES.map((s) => {
                    const meta = STAGE_CHIP_META[s];
                    const active = stage === s;
                    const count = (stageCounts || {})[s] || 0;
                    return (
                        <button
                            key={s}
                            data-testid={`filter-stage-${s.toLowerCase()}`}
                            onClick={() => setStage(active ? "" : s)}
                            className={`shrink-0 inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-[11px] font-black uppercase tracking-wider transition-colors ${
                                active ? meta.active : meta.cls
                            }`}
                        >
                            {s}
                            <span className={`inline-flex items-center justify-center rounded-full text-[9px] px-1.5 min-w-[18px] h-[16px] ${active ? "bg-white/25" : "bg-black/5 text-ink/70"}`}>
                                {count}
                            </span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}



// V2.0.9 — Desktop priority pill (mirrors the mobile one). Small colored
// pill placed inline with the name so scanning the desktop grid also
// surfaces the actionable state.
//
// V2.0.33 — the *filter chip* rows for Priority / Type (origin) /
// Signals were removed on Feb 22, 2026 (they duplicated the Superadmin
// Candidate Signals portal). These row-level pill visuals stay — they
// are purely informational badges the recruiter uses to scan the list.
const _DESKTOP_PILL_META = {
    hot:          { label: "Hot",          cls: "bg-red-500 text-white" },
    boosted:      { label: "Boost",        cls: "bg-amber-500 text-white" },
    ready:        { label: "Ready",        cls: "bg-emerald-500 text-white" },
    needs_nudge:  { label: "Nudge",        cls: "bg-yellow-400 text-yellow-900" },
    awaiting_reg: { label: "Awaiting",     cls: "bg-purple-500 text-white" },
    cold:         { label: "Cold",         cls: "bg-slate-400 text-white" },
    blocked:      { label: "Blocked",      cls: "bg-ink text-white" },
};
function DesktopPriorityPill({ priority, testid }) {
    if (!priority || !priority.bucket) return null;
    const meta = _DESKTOP_PILL_META[priority.bucket];
    if (!meta) return null;
    return (
        <span
            data-testid={testid}
            title={priority.next_action || meta.label}
            className={`inline-flex items-center text-[9px] font-black uppercase tracking-wider px-1.5 h-4 rounded-full shrink-0 ${meta.cls}`}
        >
            {meta.label}
        </span>
    );
}

function MobileCandidateRow({ candidate: c, jobMap, selected, onToggleSelect, showAvatars = true }) {
    // V2.0.45 — used by the owner-pill visibility check.
    const { user: _me } = useAuth();
    const _meId = _me?.id;
    const jobTitle = c.job_title || jobMap?.[c.job_id]?.title || null;
    const yrs = c.years_experience != null ? Number(c.years_experience) : null;
    const wm = c.work_mode_preference && c.work_mode_preference !== "Any" ? c.work_mode_preference : null;
    // V2.0.34 (Feb 22, 2026 — prod bug) — the /candidates/browse
    // aggregation projects out `resume_text` for payload reasons, so
    // relying on `!c.resume_text` alone made EVERY row falsely show
    // "NO CV" on prod. Backend now ships a cheap `has_resume_text`
    // boolean; fall back to `resume_url` / raw `resume_text` for older
    // callers.
    const noCv = !(
        c.has_resume_text
        || c.resume_text
        || c.resume_url
        || c.resume_filename
    );
    const badge = (c.verification_tier === "paid") ? null : ((c.verified_badge_status === "pending_review") ? "Verify pending" : null);
    const lastSeen = _relTime(c.last_seen_at);
    const updated = _relTime(c.updated_at);
    // V2.0.7 — priority pill at the top-right of the row so scanning stays
    // actionable even without an active filter.
    const priorityBucket = (c.priority || {}).bucket;
    const priorityLabel = (c.priority || {}).next_action;
    const priorityPillCls = {
        hot:          "bg-red-500 text-white",
        boosted:      "bg-amber-500 text-white",
        ready:        "bg-emerald-500 text-white",
        needs_nudge:  "bg-yellow-400 text-yellow-900",
        awaiting_reg: "bg-purple-500 text-white",
        cold:         "bg-slate-400 text-white",
        blocked:      "bg-ink text-white",
    }[priorityBucket] || "";
    const priorityShort = {
        hot: "Hot",
        boosted: "Boost",
        ready: "Ready",
        needs_nudge: "Nudge",
        awaiting_reg: "Reg",
        cold: "Cold",
        blocked: "Blocked",
    }[priorityBucket];
    return (
        <div className="flex gap-3 min-w-0" data-testid={`mobile-comfy-row-${c.id}`}>
            <button
                type="button"
                data-testid={`bulk-select-mobile-${c.id}`}
                onClick={onToggleSelect}
                aria-label={selected ? "Deselect candidate" : "Select candidate"}
                className="mt-1 shrink-0 text-[#525252]"
            >
                {selected ? (
                    <CheckSquare size={18} weight="fill" className="text-cobalt" />
                ) : (
                    <Square size={18} weight="bold" />
                )}
            </button>
            <RowAvatar
                url={showAvatars ? c.avatar_url : undefined}
                name={c.name}
                size={44}
                isOnline={!!c.is_online}
                testid={`mobile-comfy-avatar-${c.id}`}
            />
            <div className="min-w-0 flex-1 space-y-1">
                {/* Row 1 — name + verified + boost + priority pill */}
                <div className="flex items-center gap-1.5 min-w-0">
                    <p className="font-bold text-ink truncate">{c.name}</p>
                    <VerifiedTierChip tier={c.verification_tier} size="xs" />
                    {c.is_boosted && (
                        <span
                            data-testid={`mobile-boost-chip-${c.id}`}
                            title="Boosted"
                            className="inline-flex items-center gap-0.5 text-[9px] font-black uppercase tracking-wider text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-sm"
                        >
                            <Lightning size={10} weight="fill" />
                            Boost
                        </span>
                    )}
                    {priorityShort && (
                        <span
                            data-testid={`mobile-priority-pill-${c.id}`}
                            title={priorityLabel || priorityShort}
                            className={`ml-auto shrink-0 inline-flex items-center text-[9px] font-black uppercase tracking-wider px-1.5 h-4 rounded-full ${priorityPillCls}`}
                        >
                            {priorityShort}
                        </span>
                    )}
                </div>
                {/* Row 2 — title · years */}
                <p className="text-xs text-[#525252] truncate flex items-center gap-1">
                    <Briefcase size={11} weight="bold" className="text-ink/40 shrink-0" />
                    <span className="truncate">
                        {c.current_title || "—"}
                        {yrs != null && ` · ${yrs} yr${yrs === 1 ? "" : "s"}`}
                    </span>
                </p>
                {/* Row 3 — location · work-mode */}
                {(c.location || wm) && (
                    <p className="text-[11px] text-ink/50 truncate flex items-center gap-1">
                        <MapPin size={11} weight="bold" className="text-ink/40 shrink-0" />
                        <span className="truncate">
                            {c.location || "—"}
                            {wm && ` · ${wm}`}
                        </span>
                    </p>
                )}
                {/* Row 4 — differentiator row: origin · stage · reqs · owner */}
                <div className="flex items-center gap-1.5 flex-wrap pt-0.5">
                    <OriginChip origin={c.origin} source={c.source} testId={`mobile-origin-chip-${c.id}`} size="md" />
                    <StageChip stage={c.stage} />
                    {(c.posting_count || 1) > 1 && (
                        <span className="text-[9px] font-black uppercase tracking-wider bg-cobalt/10 text-cobalt px-1.5 py-0.5 rounded-sm">
                            {c.posting_count} reqs
                        </span>
                    )}
                    {/* V2.0.45 — owner pill so a recruiter with workspace-
                        wide visibility knows a teammate is already on this
                        candidate. Only shown when the row is owned by
                        someone other than the caller. */}
                    {c.owner_name && c.owner_id !== _meId && (
                        <span
                            data-testid={`mobile-owner-chip-${c.id}`}
                            title={`Owned by ${c.owner_name} — coordinate before reaching out`}
                            className="inline-flex items-center gap-0.5 text-[9px] font-black uppercase tracking-wider text-cobalt bg-cobalt/10 px-1.5 py-0.5 rounded-sm"
                        >
                            <User size={9} weight="bold" /> {c.owner_name}
                        </span>
                    )}
                    {/* V2.0.46 — chat activity signal. Shows when the
                        candidate has any DaddyTalk message, so recruiters
                        don't double-outreach a warm candidate. */}
                    {c.last_chat_at && (() => {
                        const rel = _relTime(c.last_chat_at);
                        return rel ? (
                            <span
                                data-testid={`mobile-lastchat-chip-${c.id}`}
                                title={`Last chat activity ${rel}`}
                                className="inline-flex items-center gap-0.5 text-[9px] font-black uppercase tracking-wider text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded-sm"
                            >
                                <ChatCircleDots size={9} weight="bold" /> Msg'd {rel}
                            </span>
                        ) : null;
                    })()}
                </div>
                {/* Row 5 — pending signals + recency */}
                {(noCv || badge || lastSeen || updated) && (
                    <div className="flex items-center gap-1.5 flex-wrap pt-0.5">
                        {noCv && (
                            <span
                                data-testid={`mobile-nocv-chip-${c.id}`}
                                className="inline-flex items-center gap-0.5 text-[9px] font-black uppercase tracking-wider text-red-700 bg-red-100 px-1.5 py-0.5 rounded-sm"
                            >
                                No CV
                            </span>
                        )}
                        {badge && (
                            <span className="inline-flex items-center gap-0.5 text-[9px] font-black uppercase tracking-wider text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-sm">
                                {badge}
                            </span>
                        )}
                        {c.is_online ? (
                            <span className="text-[10px] text-emerald-700 font-bold">● Online now</span>
                        ) : lastSeen ? (
                            <span className="text-[10px] text-ink/45">Seen {lastSeen}</span>
                        ) : updated ? (
                            <span className="text-[10px] text-ink/45">Active {updated}</span>
                        ) : null}
                    </div>
                )}
                {/* Row 6 — tags */}
                {(c.tags || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 pt-0.5">
                        {(c.tags || []).slice(0, 3).map((t) => (
                            <span key={t} className="text-[9px] font-black uppercase tracking-wider bg-[#F5F5F5] text-[#525252] px-1.5 py-0.5 rounded-sm">
                                {t}
                            </span>
                        ))}
                        {(c.tags || []).length > 3 && (
                            <span className="text-[9px] font-black uppercase tracking-wider text-[#A3A3A3]">
                                +{c.tags.length - 3}
                            </span>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}


// V2.0.4 — Candidate CSV / Excel Import Modal.
function CandidateImportModal({ open, onClose, onDone }) {
    const [file, setFile] = useState(null);
    const [detect, setDetect] = useState(null);   // V2.0.9 — {file_headers, canonical_fields, suggested_mapping, total_rows, sample_rows}
    const [mapping, setMapping] = useState({});    // {canonical: file_header}
    const [preview, setPreview] = useState(null);
    const [importing, setImporting] = useState(false);
    const [done, setDone] = useState(null);

    useEffect(() => {
        if (!open) {
            setFile(null); setDetect(null); setMapping({});
            setPreview(null); setImporting(false); setDone(null);
        }
    }, [open]);

    // V2.0.9 — run column detection first
    const runDetect = async (f) => {
        if (!f) return;
        setFile(f); setDetect(null); setMapping({}); setPreview(null); setDone(null);
        setImporting(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            const r = await api.post("/candidates/import/detect", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setDetect(r.data);
            setMapping(r.data.suggested_mapping || {});
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not read file");
            setFile(null);
        } finally {
            setImporting(false);
        }
    };

    // Confirm the column mapping → run dry-run preview
    const runPreview = async () => {
        if (!file) return;
        if (!mapping.name) {
            toast.error("Map the 'Name' column before continuing");
            return;
        }
        setImporting(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("mapping", JSON.stringify(mapping));
            const r = await api.post("/candidates/import?dry_run=1", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setPreview(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Preview failed");
        } finally {
            setImporting(false);
        }
    };

    const runImport = async () => {
        if (!file) return;
        setImporting(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("mapping", JSON.stringify(mapping));
            const r = await api.post("/candidates/import", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setDone(r.data);
            toast.success(`Imported ${r.data.created} candidate${r.data.created === 1 ? "" : "s"}`);
            onDone?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Import failed");
        } finally {
            setImporting(false);
        }
    };

    if (!open) return null;
    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-lg" data-testid="candidate-import-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Upload size={18} weight="bold" className="text-emerald-500" />
                        Import Candidates
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-3 text-sm">
                    {!file && (
                        <>
                            <div className="rounded-lg border-2 border-dashed border-ink/15 p-6 text-center">
                                <Upload size={28} weight="bold" className="mx-auto text-ink/40 mb-2" />
                                <p className="font-bold text-ink mb-1">Drop or select a .csv or .xlsx file</p>
                                <p className="text-xs text-ink/50 mb-3">Up to 5,000 rows · 8 MB max</p>
                                <input
                                    data-testid="candidate-import-file-input"
                                    type="file"
                                    accept=".csv,.xlsx,.xlsm,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                    onChange={(e) => runDetect(e.target.files?.[0])}
                                    className="block mx-auto text-xs"
                                />
                            </div>
                            <div className="rounded-md bg-black/5 p-3 text-[11px] text-ink/60">
                                <b>Required column:</b> name<br />
                                <b>Optional columns:</b> email, phone, current_title, location, years_experience, skills (comma-sep), linkedin, source, stage, tags (comma-sep)
                            </div>
                            <div className="flex items-center gap-3 justify-center text-[11px] pt-1">
                                <span className="text-ink/50 font-bold uppercase tracking-wider">Download sample:</span>
                                <a
                                    data-testid="candidate-import-sample-csv"
                                    href={`${process.env.REACT_APP_BACKEND_URL}/api/candidates/import/sample.csv`}
                                    download
                                    className="inline-flex items-center gap-1 font-black uppercase tracking-wider text-emerald-600 hover:text-emerald-700 hover:underline"
                                >
                                    <FileText size={12} weight="bold" />
                                    CSV
                                </a>
                                <span className="text-ink/20">·</span>
                                <a
                                    data-testid="candidate-import-sample-xlsx"
                                    href={`${process.env.REACT_APP_BACKEND_URL}/api/candidates/import/sample.xlsx`}
                                    download
                                    className="inline-flex items-center gap-1 font-black uppercase tracking-wider text-emerald-600 hover:text-emerald-700 hover:underline"
                                >
                                    <FileText size={12} weight="bold" />
                                    Excel
                                </a>
                            </div>
                        </>
                    )}
                    {importing && !detect && (
                        <div className="text-center py-6 text-ink/60 text-sm">Reading file…</div>
                    )}

                    {/* V2.0.9 — column-mapping step (auto-suggested, user confirms) */}
                    {detect && !preview && !done && (
                        <div className="space-y-3" data-testid="candidate-import-mapping-step">
                            <div className="rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-[11px] text-emerald-800">
                                <b>{detect.total_rows}</b> row{detect.total_rows === 1 ? "" : "s"} detected · file uses {detect.file_headers.length} columns. Confirm the mapping below.
                            </div>
                            <div className="grid grid-cols-1 gap-1.5 max-h-72 overflow-y-auto">
                                {detect.canonical_fields.map((canon) => {
                                    const isRequired = canon === "name";
                                    return (
                                        <div key={canon} className="flex items-center gap-2 text-xs">
                                            <label className="w-32 shrink-0 font-black uppercase tracking-wider text-ink/60">
                                                {canon.replace(/_/g, " ")}
                                                {isRequired && <span className="text-red-500 ml-0.5">*</span>}
                                            </label>
                                            <select
                                                data-testid={`import-mapping-${canon}`}
                                                value={mapping[canon] || ""}
                                                onChange={(e) => setMapping({ ...mapping, [canon]: e.target.value })}
                                                className={`flex-1 h-8 px-2 rounded border text-xs focus:outline-none focus:ring-1 ${
                                                    isRequired && !mapping[canon]
                                                        ? "border-red-300 focus:border-red-400 focus:ring-red-400 bg-red-50"
                                                        : "border-ink/15 focus:border-emerald-400 focus:ring-emerald-400 bg-white"
                                                }`}
                                            >
                                                <option value="">— skip —</option>
                                                {detect.file_headers.map((h) => (
                                                    <option key={h} value={h}>{h}</option>
                                                ))}
                                            </select>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="flex justify-end gap-2 pt-1">
                                <button
                                    type="button"
                                    onClick={() => { setFile(null); setDetect(null); setMapping({}); }}
                                    className="px-3 h-9 rounded-full bg-black/5 hover:bg-black/10 text-xs font-bold uppercase tracking-wider"
                                    data-testid="candidate-import-reset-btn"
                                >
                                    Choose different file
                                </button>
                                <button
                                    type="button"
                                    onClick={runPreview}
                                    disabled={importing || !mapping.name}
                                    className="px-4 h-9 rounded-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5"
                                    data-testid="candidate-import-preview-btn"
                                >
                                    {importing ? "Previewing…" : "Preview import →"}
                                </button>
                            </div>
                        </div>
                    )}

                    {preview && !done && (
                        <div className="space-y-2">
                            <div className="grid grid-cols-3 gap-2 text-center">
                                <div className="rounded-lg bg-emerald-50 border border-emerald-200 py-2">
                                    <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">To create</div>
                                    <div className="text-2xl font-black text-emerald-700" data-testid="import-preview-created">{preview.created}</div>
                                </div>
                                <div className="rounded-lg bg-amber-50 border border-amber-200 py-2">
                                    <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700">Duplicates</div>
                                    <div className="text-2xl font-black text-amber-700" data-testid="import-preview-skipped">{preview.skipped}</div>
                                </div>
                                <div className="rounded-lg bg-red-50 border border-red-200 py-2">
                                    <div className="text-[10px] font-bold uppercase tracking-wider text-red-700">Errors</div>
                                    <div className="text-2xl font-black text-red-700" data-testid="import-preview-errors">{preview.errors}</div>
                                </div>
                            </div>
                            {preview.errors > 0 && (
                                <div className="rounded-md bg-red-50 border border-red-200 p-2 max-h-40 overflow-y-auto">
                                    <div className="text-[11px] font-bold uppercase tracking-wider text-red-700 mb-1">Row-level errors</div>
                                    <ul className="text-[11px] text-red-800 space-y-0.5">
                                        {(preview.error_rows || []).map((er, i) => (
                                            <li key={i}>Row {er.row}: {er.error}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            <div className="flex justify-end gap-2 pt-2">
                                <button
                                    type="button"
                                    onClick={() => { setPreview(null); }}
                                    className="px-3 h-9 rounded-full bg-black/5 hover:bg-black/10 text-xs font-bold uppercase tracking-wider"
                                    data-testid="candidate-import-back-to-mapping"
                                >
                                    ← Back to mapping
                                </button>
                                <button
                                    type="button"
                                    onClick={runImport}
                                    disabled={importing || preview.created === 0}
                                    className="px-4 h-9 rounded-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5"
                                    data-testid="candidate-import-confirm-btn"
                                >
                                    {importing ? "Importing…" : `Import ${preview.created} candidate${preview.created === 1 ? "" : "s"}`}
                                </button>
                            </div>
                        </div>
                    )}
                    {done && (
                        <div className="text-center py-4">
                            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                                <Upload size={20} weight="bold" className="text-emerald-600" />
                            </div>
                            <p className="text-lg font-black text-ink" data-testid="candidate-import-done">Imported {done.created} candidates</p>
                            <p className="text-xs text-ink/60">
                                Skipped {done.skipped} duplicate{done.skipped === 1 ? "" : "s"} · {done.errors} error{done.errors === 1 ? "" : "s"}
                            </p>
                            <button
                                type="button"
                                onClick={onClose}
                                className="mt-4 px-4 h-9 rounded-full bg-black/5 hover:bg-black/10 text-xs font-bold uppercase tracking-wider"
                            >
                                Done
                            </button>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}




function RowKebab({ candidate, onDone, onOpenCandidate, onOpen360 }) {
    const [busy, setBusy] = useState(false);
    const [movingId, setMovingId] = useState(null);
    const [sheetOpen, setSheetOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    // Schedule-interview state — carries the specific posting to schedule for.
    const [scheduleFor, setScheduleFor] = useState(null);   // { candidate_id, name, email, stage }
    useEffect(() => {
        const mm = window.matchMedia("(max-width: 639px)");
        const update = () => setIsMobile(mm.matches);
        update();
        mm.addEventListener("change", update);
        return () => mm.removeEventListener("change", update);
    }, []);
    const postings = candidate.postings || [
        { candidate_id: candidate.id, job_id: candidate.job_id, job_title: candidate.job_title, client_name: candidate.client_name, stage: candidate.stage },
    ];

    // Wrap a posting into the shape ScheduleInterviewModal expects. If the
    // candidate isn't already in the Interview stage, silently move them first
    // (backend requires Interview stage before scheduling).
    const openSchedule = async (p) => {
        setSheetOpen(false);
        // Feb 2026 — Mandatory-registration gate for interview scheduling.
        if (!p.candidate_user_id && p.drip_state?.status === "active") {
            toast.info("Candidate hasn't registered yet — send a reminder before scheduling an interview.", { duration: 5000 });
            return;
        }
        if (p.stage !== "Interview") {
            try {
                await api.patch(`/candidates/${p.candidate_id}/stage`, { stage: "Interview" });
                toast.info(`Moved to Interview stage on ${p.job_title || "requirement"}.`);
                onDone?.();
            } catch (err) {
                toast.error(err?.response?.data?.detail || "Couldn't move to Interview stage.");
                return;
            }
        }
        setScheduleFor({
            id: p.candidate_id,
            name: candidate.name,
            email: candidate.email,
            stage: "Interview",
            job_id: p.job_id,
            job_title: p.job_title,
            client_name: p.client_name,
        });
    };
    const handleScheduled = () => {
        setScheduleFor(null);
        onDone?.();
    };

    const request = async (e) => {
        e?.preventDefault?.();
        if (busy) return;
        setBusy(true);
        try {
            const res = await api.post(`/candidates/${candidate.id}/request-testimonial`, { note: null });
            const d = res.data || {};
            if (d.already_has_testimonial) {
                toast.info(d.message || "Candidate already submitted a testimonial.");
            } else if (d.duplicate) {
                toast.info("A pending testimonial request already exists for this candidate.");
            } else {
                toast.success(
                    candidate.stage === "Hired"
                        ? "Request sent — candidate can submit right away."
                        : "Request queued. It'll unlock once the candidate is Hired.",
                );
            }
            onDone?.();
        } catch (err) {
            const detail = err?.response?.data?.detail || "Couldn't send request.";
            const fixId = err?.response?.headers?.["x-fix-job-id"];
            if (fixId) {
                toast.error(detail, {
                    duration: 12000,
                    action: {
                        label: "Fix requirement",
                        onClick: () => { window.location.href = `/jobs/${fixId}`; },
                    },
                });
            } else {
                toast.error(detail);
            }
        } finally {
            setBusy(false);
        }
    };

    const moveStage = async (p, e) => {
        e?.stopPropagation?.();
        const next = nextStageOf(p.stage);
        if (!next) return;
        // Feb 2026 — Mandatory-registration gate. Give a friendly heads-up
        // instead of hitting the 409 and rolling back with a scary error.
        const gated =
            !p.candidate_user_id &&
            p.drip_state?.status === "active" &&
            !["Applied", "Rejected"].includes(next);
        if (gated) {
            toast.info(
                `${p.name || "Candidate"} hasn't registered yet — send a reminder before moving to ${next}.`,
                { duration: 5000 },
            );
            return;
        }
        const prevStage = p.stage;
        setMovingId(p.candidate_id);
        try {
            await api.patch(`/candidates/${p.candidate_id}/stage`, { stage: next });
            toast.success(`Moved to ${next} on ${p.job_title || "requirement"}.`, {
                duration: 5000,
                action: {
                    label: "Undo",
                    onClick: async () => {
                        try {
                            await api.patch(`/candidates/${p.candidate_id}/stage`, { stage: prevStage });
                            toast(`Reverted to ${prevStage}.`);
                            onDone?.();
                        } catch (ex) {
                            toast.error(ex?.response?.data?.detail || "Undo failed.");
                        }
                    },
                },
            });
            onDone?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't update stage.");
        } finally {
            setMovingId(null);
        }
    };

    return (
        <>
            {scheduleFor && (
                <ScheduleInterviewModal
                    candidate={scheduleFor}
                    job={null}
                    onClose={() => setScheduleFor(null)}
                    onScheduled={handleScheduled}
                />
            )}
            {isMobile ? (
                <>
                    {/* Mobile: bottom sheet — thumb-reachable native feel */}
                    <button
                        type="button"
                        data-testid={`candidate-kebab-${candidate.id}`}
                        aria-label="Row actions"
                        onClick={(e) => { e.stopPropagation(); setSheetOpen(true); }}
                        className="p-1.5 hover:bg-[#E5E5E5] text-[#525252] rounded"
                    >
                        <DotsThreeVertical size={16} weight="bold" />
                    </button>

                    <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
                        <SheetContent
                            side="bottom"
                            data-testid={`candidate-kebab-sheet-${candidate.id}`}
                            className="p-0 max-h-[80vh] rounded-t-2xl bg-white"
                        >
                            {/* Grabber handle */}
                            <div className="pt-2 pb-1 flex justify-center">
                                <div className="w-10 h-1 rounded-full bg-[#E5E5E5]" />
                            </div>
                            <SheetHeader className="px-4 pt-1 pb-3 border-b border-[#F5F5F5] text-left">
                                <SheetTitle className="text-base font-black text-ink truncate">
                                    {candidate.name}
                                </SheetTitle>
                                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3]">
                                    On {postings.length} requirement{postings.length === 1 ? "" : "s"}
                                </p>
                            </SheetHeader>
                            <div className="overflow-y-auto max-h-[65vh]">
                                {/* Postings list */}
                                <div className="p-3 space-y-1">
                                    {postings.map((p) => {
                                        const next = nextStageOf(p.stage);
                                        const busyThis = movingId === p.candidate_id;
                                        return (
                                            <div
                                                key={p.candidate_id}
                                                data-testid={`sheet-posting-${p.candidate_id}`}
                                                className="border border-[#E5E5E5] p-3"
                                            >
                                                <div className="flex items-start justify-between gap-2">
                                                    <button
                                                        type="button"
                                                        data-testid={`sheet-posting-open-${p.candidate_id}`}
                                                        onClick={() => { setSheetOpen(false); onOpenCandidate?.(p.candidate_id); }}
                                                        className="min-w-0 flex-1 text-left"
                                                    >
                                                        <p className="text-sm font-bold text-ink truncate leading-tight">
                                                            {p.job_title || "—"}
                                                        </p>
                                                        {p.client_name && (
                                                            <p className="text-[10px] text-[#A3A3A3] truncate mt-0.5">
                                                                {p.client_name}
                                                            </p>
                                                        )}
                                                    </button>
                                                    <span className={`text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 shrink-0 ${
                                                        {
                                                            Applied: "bg-[#F5F5F5] text-[#525252]",
                                                            Screening: "bg-[#EEF2FF] text-[#3730A3]",
                                                            Interview: "bg-[#EFF6FF] text-[#F59E0B]",
                                                            Offer: "bg-[#FAF5FF] text-[#7E22CE]",
                                                            Hired: "bg-[#ECFDF5] text-[#059669]",
                                                            Rejected: "bg-[#FEF2F2] text-[#DC2626]",
                                                        }[p.stage] || "bg-[#F5F5F5] text-[#525252]"
                                                    }`}>
                                                        {p.stage}
                                                    </span>
                                                </div>
                                                {next && (
                                                    <button
                                                        type="button"
                                                        data-testid={`sheet-move-next-${p.candidate_id}`}
                                                        onClick={() => moveStage(p)}
                                                        disabled={busyThis}
                                                        className="mt-2 w-full py-2 text-[11px] font-black uppercase tracking-wider bg-[#F9F9F9] border border-[#E5E5E5] text-[#525252] hover:border-ink hover:text-ink disabled:opacity-50 inline-flex items-center justify-center gap-1"
                                                    >
                                                        Move to {next} <span aria-hidden>→</span>
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    data-testid={`sheet-schedule-${p.candidate_id}`}
                                                    onClick={() => openSchedule(p)}
                                                    className="mt-2 w-full py-2 text-[11px] font-black uppercase tracking-wider bg-cobalt text-white inline-flex items-center justify-center gap-1"
                                                >
                                                    <CalendarBlank size={12} weight="bold" />
                                                    Schedule interview
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Quick actions */}
                                <div className="border-t border-[#F5F5F5] p-3 space-y-1">
                                    <button
                                        type="button"
                                        data-testid={`sheet-view-360-${candidate.id}`}
                                        onClick={() => { setSheetOpen(false); onOpen360?.(candidate.id); }}
                                        className="w-full text-left px-3 py-3 hover:bg-[#F9F9F9] text-sm font-medium text-ink inline-flex items-center gap-2"
                                    >
                                        <User size={14} weight="bold" className="text-[#525252]" />
                                        View 360° profile
                                    </button>
                                    <button
                                        type="button"
                                        data-testid={`sheet-request-testimonial-${candidate.id}`}
                                        onClick={() => { setSheetOpen(false); request(); }}
                                        disabled={busy}
                                        className="w-full text-left px-3 py-3 hover:bg-[#F9F9F9] text-sm font-medium text-ink inline-flex items-center gap-2 disabled:opacity-50"
                                    >
                                        <ChatCircleDots size={14} weight="bold" className="text-[#525252]" />
                                        {busy ? "Sending…" : "Request testimonial"}
                                    </button>
                                </div>
                            </div>
                        </SheetContent>
                    </Sheet>
                </>
            ) : (
            <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    data-testid={`candidate-kebab-${candidate.id}`}
                    aria-label="Row actions"
                    onClick={(e) => e.stopPropagation()}
                    className="p-1.5 hover:bg-[#E5E5E5] text-[#525252] rounded"
                >
                    <DotsThreeVertical size={16} weight="bold" />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                align="end"
                sideOffset={4}
                collisionPadding={12}
                className="w-[calc(100vw-24px)] max-w-[320px] sm:w-[340px] sm:max-w-none p-1.5 bg-white"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Postings section */}
                <div className="px-1.5 py-1">
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3] mb-1.5">
                        On {postings.length} requirement{postings.length === 1 ? "" : "s"}
                    </p>
                    <div className="flex flex-col gap-0.5">
                        {postings.map((p) => {
                            const next = nextStageOf(p.stage);
                            const busyThis = movingId === p.candidate_id;
                            return (
                                <div
                                    key={p.candidate_id}
                                    data-testid={`kebab-posting-${p.candidate_id}`}
                                    className="flex items-center gap-1.5 px-1.5 py-1.5 hover:bg-[#F9F9F9] rounded"
                                >
                                    <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); onOpenCandidate?.(p.candidate_id); }}
                                        className="min-w-0 flex-1 text-left"
                                        data-testid={`kebab-posting-open-${p.candidate_id}`}
                                    >
                                        <p className="text-xs font-bold text-ink truncate leading-tight">
                                            {p.job_title || "—"}
                                        </p>
                                        {p.client_name && (
                                            <p className="text-[10px] text-[#A3A3A3] truncate leading-tight">
                                                {p.client_name}
                                            </p>
                                        )}
                                    </button>
                                    <span className={`text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 shrink-0 ${
                                        {
                                            Applied: "bg-[#F5F5F5] text-[#525252]",
                                            Screening: "bg-[#EEF2FF] text-[#3730A3]",
                                            Interview: "bg-[#EFF6FF] text-[#F59E0B]",
                                            Offer: "bg-[#FAF5FF] text-[#7E22CE]",
                                            Hired: "bg-[#ECFDF5] text-[#059669]",
                                            Rejected: "bg-[#FEF2F2] text-[#DC2626]",
                                        }[p.stage] || "bg-[#F5F5F5] text-[#525252]"
                                    }`}>
                                        {p.stage}
                                    </span>
                                    {next && (
                                        <button
                                            type="button"
                                            data-testid={`kebab-move-next-${p.candidate_id}`}
                                            onClick={(e) => moveStage(p, e)}
                                            disabled={busyThis}
                                            title={`Move to ${next}`}
                                            className="text-[9px] font-black uppercase tracking-wider px-1.5 py-1 border border-[#E5E5E5] text-[#525252] hover:border-ink hover:text-ink disabled:opacity-50 shrink-0"
                                        >
                                            → {next.slice(0, 3)}
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        data-testid={`kebab-schedule-${p.candidate_id}`}
                                        onClick={(e) => { e.stopPropagation(); openSchedule(p); }}
                                        title="Schedule interview"
                                        className="text-[9px] font-black uppercase tracking-wider p-1.5 bg-cobalt text-white hover:opacity-90 shrink-0 inline-flex items-center gap-0.5"
                                    >
                                        <CalendarBlank size={11} weight="bold" />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <div className="border-t border-[#F5F5F5] my-1" />
                <DropdownMenuItem
                    data-testid={`kebab-request-testimonial-${candidate.id}`}
                    onSelect={request}
                    disabled={busy}
                    className="text-xs"
                >
                    <ChatCircleDots size={13} weight="bold" className="mr-2" />
                    {busy ? "Sending…" : "Request testimonial"}
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
            )}
        </>
    );
}

function StageChip({ stage }) {
    const map = {
        Applied: "bg-[#F5F5F5] text-[#525252]",
        Screening: "bg-[#EEF2FF] text-[#3730A3]",
        Interview: "bg-[#EFF6FF] text-[#F59E0B]",
        Offer: "bg-[#FAF5FF] text-[#7E22CE]",
        Hired: "bg-[#ECFDF5] text-[#059669]",
        Rejected: "bg-[#FEF2F2] text-[#DC2626]",
    };
    return (
        <span
            className={`text-[10px] font-black uppercase tracking-wider px-2 py-1 ${map[stage] || ""}`}
        >
            {stage}
        </span>
    );
}

// ------- Small controlled Select-like <select> with consistent styling -------
function BareSelect({ value, onChange, options, testId, ariaLabel, className = "" }) {
    return (
        <select
            data-testid={testId}
            aria-label={ariaLabel}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className={`h-9 px-2 pr-7 text-xs font-medium bg-white border border-[#E5E5E5] text-ink focus:outline-none focus:border-cobalt focus:ring-1 focus:ring-cobalt ${className}`}
        >
            {options.map((o) => (
                <option key={String(o.value)} value={o.value}>{o.label}</option>
            ))}
        </select>
    );
}

// ------- Advanced filter drawer content --------------------------------------
function AdvancedPanel({
    skills, setSkills,
    location, setLocation,
    minYrs, setMinYrs, maxYrs, setMaxYrs,
    minSalary, setMinSalary, maxSalary, setMaxSalary,
    source, setSource,
    tags, setTags,
    jobId, setJobId,
    clientId, setClientId,
    addedWithin, setAddedWithin,
    facets,
    jobs,
    clients,
}) {
    const jobOptions = useMemo(
        () => [{ value: "", label: "Any requirement" }, ...jobs.map((j) => ({ value: j.id, label: j.title }))],
        [jobs],
    );
    const clientOptions = useMemo(
        () => [{ value: "", label: "Any client" }, ...clients.map((c) => ({ value: c.id, label: c.name }))],
        [clients],
    );
    // V1.5.5.17 — Canonical-only Location dropdown (matches JobsList behaviour).
    // Previously we merged raw facet strings ("Bangalore Whitefield",
    // "Delhi NCR", "Mumbai Ghansoli", …) with IN_CITIES which polluted
    // the picker with dupes and typos. Now the dropdown surfaces:
    //   • canonical IN_CITIES that ANY in-DB free-text substring-matches
    //     (so "Bangalore Whitefield" rolls up under "Bangalore"), shown
    //     first as "in-use" cities.
    //   • plus the full IN_CITIES set as a fallback so recruiters can
    //     still search a city that has no candidates yet.
    // The backend `location` filter uses a substring regex (see
    // candidate_search.py) so picking a canonical city catches every
    // free-text variant that contains it.
    const locOptions = useMemo(() => {
        const canonSet = new Set(IN_CITIES.map((c) => c.toLowerCase()));
        const inUse = new Set();       // canonical cities present in-DB
        for (const raw of facets.locations || []) {
            const v = (raw || "").trim();
            if (!v) continue;
            const lc = v.toLowerCase();
            if (canonSet.has(lc)) {
                // Exact canonical match — record with correct casing.
                for (const c of IN_CITIES) {
                    if (c.toLowerCase() === lc) { inUse.add(c); break; }
                }
                continue;
            }
            // Substring rollup — "Bangalore Whitefield" → "Bangalore".
            for (const c of IN_CITIES) {
                if (lc.includes(c.toLowerCase())) { inUse.add(c); break; }
            }
        }
        const inUseArr = [...inUse].sort().map((c) => ({ value: c, label: c }));
        const rest = IN_CITIES.filter((c) => !inUse.has(c)).map((c) => ({ value: c, label: c }));
        return [
            { value: "", label: "Any location" },
            ...inUseArr,
            ...rest,
        ];
    }, [facets.locations]);
    const tagOptions = facets.tags || [];

    return (
        <div
            data-testid="candidate-advanced-panel"
            className="bg-white border border-[#E5E5E5] p-4 sm:p-5 mb-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
        >
            {/* V2.0.7 — Kanban backlink. Stage-level filtering + drag/drop
                moved to /kanban; this banner surfaces the redirect. */}
            <div className="md:col-span-2 lg:col-span-3">
                <Link
                    to="/kanban"
                    data-testid="advanced-kanban-link"
                    className="flex items-center justify-between gap-3 px-4 py-3 rounded-lg bg-gradient-to-r from-cobalt/10 via-emerald-50 to-emerald-50 border border-cobalt/20 hover:from-cobalt/15 hover:via-emerald-100 hover:to-emerald-100 transition-colors"
                >
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-lg bg-cobalt text-white flex items-center justify-center shrink-0">
                            <Rows size={16} weight="bold" />
                        </div>
                        <div className="min-w-0">
                            <div className="text-sm font-black text-ink truncate">Manage stages on the Kanban board</div>
                            <div className="text-[11px] text-ink/60 truncate">Drag-and-drop through Applied → Hired · Filter by stage · See pipeline visually</div>
                        </div>
                    </div>
                    <span className="shrink-0 inline-flex items-center gap-1 text-xs font-black uppercase tracking-wider text-cobalt">
                        Open Kanban <ArrowRight size={12} weight="bold" />
                    </span>
                </Link>
            </div>

            {/* Skills */}
            <div className="md:col-span-2 lg:col-span-3">
                <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-[#525252] mb-1.5">
                    Skills
                </label>
                <SkillsChipInput
                    value={skills}
                    onChange={setSkills}
                    testId="candidate-search-skills"
                    placeholder="e.g. python, react, sql — press Enter"
                />
            </div>

            {/* Location */}
            <div>
                <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-[#525252] mb-1.5">
                    Location
                </label>
                <BareSelect
                    value={location}
                    onChange={setLocation}
                    options={locOptions}
                    testId="candidate-search-location"
                    ariaLabel="Location"
                    className="w-full"
                />
            </div>

            {/* Years experience */}
            <div>
                <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-[#525252] mb-1.5">
                    Experience (years)
                </label>
                <div className="flex items-center gap-2">
                    <input
                        data-testid="candidate-search-min-yrs"
                        type="number"
                        min="0"
                        step="0.5"
                        value={minYrs}
                        onChange={(e) => setMinYrs(e.target.value)}
                        placeholder="Min"
                        className="w-full h-9 px-2 text-xs bg-white border border-[#E5E5E5] focus:outline-none focus:border-cobalt focus:ring-1 focus:ring-cobalt"
                    />
                    <span className="text-[#A3A3A3] text-xs">to</span>
                    <input
                        data-testid="candidate-search-max-yrs"
                        type="number"
                        min="0"
                        step="0.5"
                        value={maxYrs}
                        onChange={(e) => setMaxYrs(e.target.value)}
                        placeholder="Max"
                        className="w-full h-9 px-2 text-xs bg-white border border-[#E5E5E5] focus:outline-none focus:border-cobalt focus:ring-1 focus:ring-cobalt"
                    />
                </div>
            </div>

            {/* Expected salary (INR) */}
            <div>
                <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-[#525252] mb-1.5">
                    Expected salary (₹)
                </label>
                <div className="flex items-center gap-2">
                    <input
                        data-testid="candidate-search-min-salary"
                        type="number"
                        min="0"
                        step="1000"
                        value={minSalary}
                        onChange={(e) => setMinSalary(e.target.value)}
                        placeholder="Min"
                        className="w-full h-9 px-2 text-xs bg-white border border-[#E5E5E5] focus:outline-none focus:border-cobalt focus:ring-1 focus:ring-cobalt"
                    />
                    <span className="text-[#A3A3A3] text-xs">to</span>
                    <input
                        data-testid="candidate-search-max-salary"
                        type="number"
                        min="0"
                        step="1000"
                        value={maxSalary}
                        onChange={(e) => setMaxSalary(e.target.value)}
                        placeholder="Max"
                        className="w-full h-9 px-2 text-xs bg-white border border-[#E5E5E5] focus:outline-none focus:border-cobalt focus:ring-1 focus:ring-cobalt"
                    />
                </div>
            </div>

            {/* Requirement */}
            <div>
                <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-[#525252] mb-1.5">
                    Requirement
                </label>
                <BareSelect
                    value={jobId}
                    onChange={setJobId}
                    options={jobOptions}
                    testId="candidate-search-job"
                    ariaLabel="Requirement"
                    className="w-full"
                />
            </div>

            {/* Client */}
            <div>
                <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-[#525252] mb-1.5">
                    Client
                </label>
                <BareSelect
                    value={clientId}
                    onChange={setClientId}
                    options={clientOptions}
                    testId="candidate-search-client"
                    ariaLabel="Client"
                    className="w-full"
                />
            </div>

            {/* Source */}
            <div>
                <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-[#525252] mb-1.5">
                    Source
                </label>
                <BareSelect
                    value={source}
                    onChange={setSource}
                    options={[{ value: "", label: "Any source" }, ...SOURCES]}
                    testId="candidate-search-source"
                    ariaLabel="Source"
                    className="w-full"
                />
            </div>

            {/* Added within */}
            <div>
                <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-[#525252] mb-1.5">
                    Added
                </label>
                <BareSelect
                    value={addedWithin}
                    onChange={setAddedWithin}
                    options={ADDED_WITHIN}
                    testId="candidate-search-added-within"
                    ariaLabel="Added within"
                    className="w-full"
                />
            </div>

            {/* Tags */}
            {tagOptions.length > 0 && (
                <div className="md:col-span-2 lg:col-span-3">
                    <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-[#525252] mb-1.5">
                        Tags
                    </label>
                    <div className="flex flex-wrap gap-1.5">
                        {tagOptions.map((t) => {
                            const active = tags.includes(t);
                            return (
                                <button
                                    key={t}
                                    type="button"
                                    data-testid={`candidate-search-tag-${t}`}
                                    onClick={() =>
                                        setTags(active ? tags.filter((x) => x !== t) : [...tags, t])
                                    }
                                    className={`text-[10px] font-black uppercase tracking-wider px-2 py-1 border transition-colors ${
                                        active
                                            ? "bg-ink text-white border-ink"
                                            : "bg-white text-[#525252] border-[#E5E5E5] hover:border-ink"
                                    }`}
                                >
                                    {t}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

// ---------------------- Bulk Action Bar + Modals ----------------------------
function BulkActionBar({ count, onTag, onCopy, onExport, onReachOut, onCopyLinks, linkFormat, onLinkFormatChange, onClear, busy }) {
    return (
        <div
            data-testid="candidate-bulk-bar"
            className="fixed z-40 bg-ink text-white shadow-2xl border border-black
                       inset-x-2 bottom-16 px-3 py-2.5 flex items-center gap-2 flex-wrap justify-center
                       sm:inset-x-auto sm:bottom-4 sm:left-1/2 sm:-translate-x-1/2 sm:px-4 sm:py-3 sm:gap-3 sm:flex-nowrap"
        >
            <span className="text-xs font-black uppercase tracking-[0.2em]" data-testid="bulk-bar-count">
                {count} selected
            </span>
            <span className="hidden sm:inline h-4 w-px bg-white/20" />
            <button
                data-testid="bulk-action-reach-out"
                onClick={onReachOut}
                disabled={busy}
                className="text-xs font-bold uppercase tracking-wider hover:text-[#FCD34D] transition-colors inline-flex items-center gap-1.5 disabled:opacity-50"
            >
                <PaperPlaneTilt size={13} weight="bold" />
                <span className="hidden sm:inline">Reach out</span>
            </button>
            <button
                data-testid="bulk-action-tag"
                onClick={onTag}
                disabled={busy}
                className="text-xs font-bold uppercase tracking-wider hover:text-[#FCD34D] transition-colors inline-flex items-center gap-1.5 disabled:opacity-50"
            >
                <Tag size={13} weight="bold" />
                <span className="hidden sm:inline">Tag</span>
            </button>
            <button
                data-testid="bulk-action-copy"
                onClick={onCopy}
                disabled={busy}
                className="text-xs font-bold uppercase tracking-wider hover:text-[#FCD34D] transition-colors inline-flex items-center gap-1.5 disabled:opacity-50"
            >
                <Copy size={13} weight="bold" />
                <span className="hidden sm:inline">Copy to job</span>
            </button>
            <button
                data-testid="bulk-action-export"
                onClick={onExport}
                disabled={busy}
                className="text-xs font-bold uppercase tracking-wider hover:text-[#FCD34D] transition-colors inline-flex items-center gap-1.5 disabled:opacity-50"
            >
                <DownloadSimple size={13} weight="bold" />
                <span className="hidden sm:inline">Export CSV</span>
            </button>
            <span className="hidden sm:inline h-4 w-px bg-white/20" />
            {/* V1.4 — Bulk copy sign-in links */}
            <div className="inline-flex items-stretch">
                <select
                    value={linkFormat}
                    onChange={(e) => onLinkFormatChange(e.target.value)}
                    disabled={busy}
                    data-testid="bulk-copy-links-format"
                    title="Formatting style for the copied block"
                    className="bg-transparent border-r border-white/20 text-[10px] font-bold uppercase tracking-wider px-1.5 focus:outline-none disabled:opacity-50"
                >
                    <option value="plain" className="text-black">Plain</option>
                    <option value="whatsapp" className="text-black">WhatsApp</option>
                </select>
                <button
                    data-testid="bulk-action-copy-links"
                    onClick={onCopyLinks}
                    disabled={busy}
                    title="Mint magic sign-in links for every selected candidate, copy the formatted block to clipboard."
                    className="text-xs font-bold uppercase tracking-wider hover:text-[#FCD34D] transition-colors inline-flex items-center gap-1.5 pl-2 disabled:opacity-50"
                >
                    <LinkSimple size={13} weight="bold" />
                    <span className="hidden sm:inline">Copy links</span>
                </button>
            </div>
            <span className="hidden sm:inline h-4 w-px bg-white/20" />
            <button
                data-testid="bulk-action-clear"
                onClick={onClear}
                disabled={busy}
                className="text-xs font-bold uppercase tracking-wider text-white/70 hover:text-white inline-flex items-center gap-1 disabled:opacity-50"
            >
                <X size={12} weight="bold" />
                <span className="hidden sm:inline">Clear</span>
            </button>
        </div>
    );
}

function TagBulkModal({ open, onOpenChange, count, existingTags, onSubmit }) {
    const [selected, setSelected] = useState([]);
    const [freeText, setFreeText] = useState("");
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        if (open) { setSelected([]); setFreeText(""); }
    }, [open]);

    const submit = async () => {
        const extras = freeText.split(",").map((t) => t.trim()).filter(Boolean);
        const merged = Array.from(new Set([...selected, ...extras]));
        if (merged.length === 0) return;
        setBusy(true);
        try {
            await onSubmit(merged);
            onOpenChange(false);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="bulk-tag-modal" className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>Tag {count} candidate{count === 1 ? "" : "s"}</DialogTitle>
                </DialogHeader>
                <p className="text-xs text-[#525252] -mt-2 mb-2">
                    Tags will be <strong>added</strong> to each candidate — existing tags stay intact.
                </p>

                {existingTags.length > 0 && (
                    <div>
                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#525252] mb-1.5">Existing tags</p>
                        <div className="flex flex-wrap gap-1.5">
                            {existingTags.map((t) => {
                                const active = selected.includes(t);
                                return (
                                    <button
                                        key={t}
                                        type="button"
                                        data-testid={`bulk-tag-chip-${t}`}
                                        onClick={() => setSelected(active ? selected.filter((x) => x !== t) : [...selected, t])}
                                        className={`text-[10px] font-black uppercase tracking-wider px-2 py-1 border transition-colors ${
                                            active ? "bg-ink text-white border-ink" : "bg-white text-[#525252] border-[#E5E5E5] hover:border-ink"
                                        }`}
                                    >
                                        {t}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                <div>
                    <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-[#525252] mb-1.5">
                        Add new tags (comma-separated)
                    </label>
                    <input
                        data-testid="bulk-tag-free-text"
                        value={freeText}
                        onChange={(e) => setFreeText(e.target.value)}
                        placeholder="e.g. shortlist, followup"
                        className="w-full px-3 py-2 text-sm bg-white border border-[#E5E5E5] focus:outline-none focus:border-cobalt focus:ring-1 focus:ring-cobalt"
                    />
                </div>

                <DialogFooter>
                    <button
                        type="button"
                        onClick={() => onOpenChange(false)}
                        className="px-4 py-2 text-xs font-black uppercase tracking-wider border border-[#E5E5E5] text-[#525252] hover:border-ink"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        data-testid="bulk-tag-submit"
                        onClick={submit}
                        disabled={busy || (selected.length === 0 && !freeText.trim())}
                        className="px-4 py-2 text-xs font-black uppercase tracking-wider bg-ink text-white disabled:opacity-50"
                    >
                        {busy ? "Applying…" : "Apply tags"}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function CopyBulkModal({ open, onOpenChange, count, jobs, onSubmit }) {
    const [targetJobId, setTargetJobId] = useState("");
    const [busy, setBusy] = useState(false);
    useEffect(() => { if (open) setTargetJobId(""); }, [open]);

    const submit = async () => {
        if (!targetJobId) return;
        setBusy(true);
        try {
            await onSubmit(targetJobId);
            onOpenChange(false);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="bulk-copy-modal" className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>Copy {count} candidate{count === 1 ? "" : "s"} to another requirement</DialogTitle>
                </DialogHeader>
                <p className="text-xs text-[#525252] -mt-2 mb-2">
                    A fresh row is created on the target requirement. The original stays untouched.
                    Duplicates (same email or phone) on the target are skipped automatically.
                </p>
                <div>
                    <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-[#525252] mb-1.5">
                        Target requirement
                    </label>
                    <select
                        data-testid="bulk-copy-target-job"
                        value={targetJobId}
                        onChange={(e) => setTargetJobId(e.target.value)}
                        className="w-full h-10 px-2 pr-7 text-sm bg-white border border-[#E5E5E5] text-ink focus:outline-none focus:border-cobalt focus:ring-1 focus:ring-cobalt"
                    >
                        <option value="">Choose a requirement…</option>
                        {jobs.map((j) => (
                            <option key={j.id} value={j.id}>{j.title}</option>
                        ))}
                    </select>
                </div>
                <DialogFooter>
                    <button
                        type="button"
                        onClick={() => onOpenChange(false)}
                        className="px-4 py-2 text-xs font-black uppercase tracking-wider border border-[#E5E5E5] text-[#525252] hover:border-ink"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        data-testid="bulk-copy-submit"
                        onClick={submit}
                        disabled={busy || !targetJobId}
                        className="px-4 py-2 text-xs font-black uppercase tracking-wider bg-ink text-white disabled:opacity-50"
                    >
                        {busy ? "Copying…" : "Copy candidates"}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ---------------------- Main page --------------------------------------------
export default function CandidatesList() {
    const { user } = useAuth();
    const canSeeAllOwners = ["admin", "hiring_manager", "superadmin"].includes(user?.role);
    // Server-driven results
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);

    // Recruiter list for admin/HM's "Added by" filter dropdown.
    const [teammates, setTeammates] = useState([]);
    useEffect(() => {
        if (!canSeeAllOwners) return;
        (async () => {
            try {
                const { data } = await api.get("/users");
                setTeammates(
                    (data || []).filter((u) =>
                        ["recruiter", "admin", "hiring_manager"].includes(u.role)
                    )
                );
            } catch (_) { /* silent */ }
        })();
    }, [canSeeAllOwners]);

    // Support metadata
    const [jobs, setJobs] = useState([]);
    const [clients, setClients] = useState([]);
    const [facets, setFacets] = useState({ locations: [], tags: [], stages: [], sources: [] });

    // Query state — hydrated once from URL, then two-way synced.
    const [searchParams, setSearchParams] = useSearchParams();
    const initFromUrl = useRef(false);
    const [q, setQ] = useState(() => searchParams.get("q") || "");
    const [stage, setStage] = useState(() => searchParams.get("stage") || "");
    const [boostedOnly, setBoostedOnly] = useState(() => searchParams.get("boosted") === "1");
    const [skills, setSkills] = useState(() => {
        const s = searchParams.get("skills");
        return s ? s.split(",").filter(Boolean) : [];
    });
    const [location, setLocation] = useState(() => searchParams.get("location") || "");
    const [minYrs, setMinYrs] = useState(() => searchParams.get("min_yrs") || "");
    const [maxYrs, setMaxYrs] = useState(() => searchParams.get("max_yrs") || "");
    const [minSalary, setMinSalary] = useState(() => searchParams.get("min_salary") || "");
    const [maxSalary, setMaxSalary] = useState(() => searchParams.get("max_salary") || "");
    const [source, setSource] = useState(() => searchParams.get("source") || "");
    const [tags, setTags] = useState(() => {
        const t = searchParams.get("tags");
        return t ? t.split(",").filter(Boolean) : [];
    });
    const [jobId, setJobId] = useState(() => searchParams.get("job_id") || "");
    const [clientId, setClientId] = useState(() => searchParams.get("client_id") || "");
    const [addedWithin, setAddedWithin] = useState(() => searchParams.get("added_within") || "");
    // Feb 9, 2026 — "Added by" filter (admin/HM only view; recruiters are
    // already server-scoped to their own + team pool).
    const [addedBy, setAddedBy] = useState(() => searchParams.get("added_by") || "");
    const [sort, setSort] = useState(() => searchParams.get("sort") || "recent");

    // UI state
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [selectedId, setSelectedId] = useState(null);      // per-JD drawer (from kebab)
    const [personCid, setPersonCid] = useState(null);        // 360° panel (from row click)

    // Bulk selection state (Phase 2)
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [selectAllMatching, setSelectAllMatching] = useState(false);
    const [showTagModal, setShowTagModal] = useState(false);
    const [showCopyModal, setShowCopyModal] = useState(false);
    const [showBulkReachOut, setShowBulkReachOut] = useState(false);
    const [showStaleCvNudge, setShowStaleCvNudge] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [bulkBusy, setBulkBusy] = useState(false);

    // Workspace preference — candidate avatars in list view. The
    // authoritative source is `workspace_settings.show_candidate_avatars_in_list`
    // (fetched below). We default to `false` locally to prevent a flash of
    // photos if the admin has opted for bias-safe hiring — the API call will
    // flip this ON once the setting is loaded.
    // V2.0.5 — Priority + Origin filters (chip-driven triage)
    // V2.0.6 — Verification tier filter (kyc / paid / any)
    // V2.0.20 — Two-phase filter state: chips update `pending*` immediately
    // (snappy UI), the applied `priority` / `origin` / `verificationTier`
    // (and therefore the search API call) only change when the user taps
    // "Apply filters". Prevents the search from firing on every chip toggle
    // — a big deal on workspaces with thousands of candidates.
    // V2.0.33 (Feb 22, 2026) — filter chip rows retired. These states
    // remain wired so any legacy `?priority=` / `?origin=` bookmark
    // still filters on the server; the UI just no longer offers a way
    // to toggle them (candidate-signals portal is the source of truth
    // for these buckets going forward).
    const [priority, setPriority] = useState(() => searchParams.get("priority") || "");
    const [origin, setOrigin] = useState(() => searchParams.get("origin") || "");
    const [verificationTier, setVerificationTier] = useState(() => searchParams.get("verification_tier") || "");
    const [priorityStats, setPriorityStats] = useState(null);
    // V2.0.33 — stage chip counts sourced from /candidates/browse.
    const [stageCounts, setStageCounts] = useState({});

    const [showAvatars, setShowAvatars] = useState(false);

    // V2.0.17 — Unified density preference. Uses the shared `chat_density`
    // localStorage key so the Candidates list and all chat surfaces share
    // one cozy/compact choice. Legacy key `candidates_row_density` is
    // migrated on first mount.
    useEffect(() => {
        try {
            const legacy = localStorage.getItem("candidates_row_density");
            if (legacy && !localStorage.getItem("chat_density")) {
                localStorage.setItem("chat_density", legacy === "compact" ? "compact" : "cozy");
                localStorage.removeItem("candidates_row_density");
            }
        } catch {}
    }, []);
    const [_density, _setDensity] = useChatDensity();
    const rowDensity = _density === "compact" ? "compact" : "comfortable";
    const setRowDensity = (v) => _setDensity(v === "compact" ? "compact" : "cozy");

    // Fetch jobs + clients + facets once
    useEffect(() => {
        api.get("/jobs").then((r) => setJobs(r.data || [])).catch((e) => logError("Failed to load jobs:", e));
        api.get("/clients").then((r) => setClients(r.data || [])).catch(() => {});
        api.get("/candidates/search/facets")
            .then((r) => setFacets(r.data || { locations: [], tags: [], stages: [], sources: [] }))
            .catch((e) => logError("Failed to load facets:", e));
        api.get("/workspace/settings")
            .then((r) => setShowAvatars(!!r.data?.show_candidate_avatars_in_list))
            .catch(() => setShowAvatars(false));
        // Deep-link support: ?open=<cid> from the ⌘K command palette opens the
        // 360° person detail panel automatically once the list has data.
        const openCid = searchParams.get("open");
        if (openCid) setPersonCid(openCid);
    }, []);

    // Build search params
    const params = useMemo(() => {
        const p = { limit: 100, offset: 0, sort };
        if (q.trim()) p.q = q.trim();
        if (skills.length) p.skills = skills.join(",");
        if (location) p.location = location;
        if (minYrs !== "") p.min_yrs = minYrs;
        if (maxYrs !== "") p.max_yrs = maxYrs;
        if (minSalary !== "") p.min_salary = minSalary;
        if (maxSalary !== "") p.max_salary = maxSalary;
        if (stage) p.stage = stage;
        if (source) p.source = source;
        if (tags.length) p.tags = tags.join(",");
        if (jobId) p.job_id = jobId;
        if (clientId) p.client_id = clientId;
        if (addedWithin) p.added_within = addedWithin;
        if (priority) p.priority = priority;
        if (origin) p.origin = origin;
        if (verificationTier) p.verification_tier = verificationTier;
        return p;
    }, [q, skills, location, minYrs, maxYrs, minSalary, maxSalary, stage, source, tags, jobId, clientId, addedWithin, sort, priority, origin, verificationTier]);

    // URL sync — persist active filters in the query string so back/forward,
    // reload, and "copy link to share filtered view" all just work.
    useEffect(() => {
        const next = new URLSearchParams();
        if (q.trim()) next.set("q", q.trim());
        if (stage) next.set("stage", stage);
        if (boostedOnly) next.set("boosted", "1");
        if (skills.length) next.set("skills", skills.join(","));
        if (location) next.set("location", location);
        if (minYrs !== "") next.set("min_yrs", minYrs);
        if (maxYrs !== "") next.set("max_yrs", maxYrs);
        if (minSalary !== "") next.set("min_salary", minSalary);
        if (maxSalary !== "") next.set("max_salary", maxSalary);
        if (source) next.set("source", source);
        if (tags.length) next.set("tags", tags.join(","));
        if (jobId) next.set("job_id", jobId);
        if (clientId) next.set("client_id", clientId);
        if (addedWithin) next.set("added_within", addedWithin);
        if (priority) next.set("priority", priority);
        if (origin) next.set("origin", origin);
        if (verificationTier) next.set("verification_tier", verificationTier);
        if (sort && sort !== "recent") next.set("sort", sort);
        // Preserve any non-filter params (e.g. ?open=<cid> from ⌘K).
        const preserved = new URLSearchParams(window.location.search);
        ["open"].forEach((k) => {
            const v = preserved.get(k);
            if (v) next.set(k, v);
        });
        setSearchParams(next, { replace: true });
        // Auto-expand advanced panel on first render if URL has any advanced filter
        if (!initFromUrl.current) {
            initFromUrl.current = true;
            const advKeys = ["skills", "min_yrs", "max_yrs", "min_salary", "max_salary", "source", "tags", "job_id", "client_id", "added_within", "location"];
            if (advKeys.some((k) => searchParams.get(k))) setShowAdvanced(true);
        }
    }, [q, stage, boostedOnly, skills, location, minYrs, maxYrs, minSalary, maxSalary, source, tags, jobId, clientId, addedWithin, sort, priority, origin, verificationTier]);

    // V2.0.5 — Poll priority-stats for the filter chip counts.
    // V2.0.24 — Unified /browse endpoint: single API call returns
    // `items`, `page.total`, and `counts` (priority + origin + signals +
    // boosted). Replaces the old dual fetch of `/priority-stats` +
    // `/search`, guaranteeing chip counts and row counts come from the
    // same aggregation. See services/candidate_pipeline.py.
    const load = useCallback(() => {
        setLoading(true);
        api.get("/candidates/browse", { params: { ...params, boosted_only: boostedOnly ? 1 : 0, skip: 0, limit: 100 } })
            .then((r) => {
                const data = r.data || {};
                setItems(data.items || []);
                setTotal(data.page?.total ?? data.counts?.total ?? 0);
                // Adapt the /browse count shape to the historical
                // `priorityStats` shape the chip row reads.
                const c = data.counts || {};
                setPriorityStats({
                    totals: c.priority || {},
                    origin_totals: c.origin || {},
                    signal_totals: c.signals || {},
                    boosted: c.boosted || 0,
                    total: c.total || 0,
                });
                setStageCounts(c.stage || {});
            })
            .catch((e) => logError("Browse failed:", e))
            .finally(() => setLoading(false));
    }, [params, boostedOnly]);

    useEffect(() => {
        const t = setTimeout(load, 250);
        return () => clearTimeout(t);
    }, [load]);

    // V2.0.24 — `boosted_only` is now applied server-side inside
    // /browse, so we no longer re-filter here. `items` is the final
    // display list.
    let displayed = items;
    // Feb 9, 2026 — "Added by" filter (admin/HM only; recruiters are server-scoped).
    if (canSeeAllOwners && addedBy) {
        if (addedBy === "__pool__") {
            displayed = displayed.filter((c) => c.is_team_pool);
        } else if (addedBy === "__me__") {
            displayed = displayed.filter((c) => c.owner_id === user?.id);
        } else {
            displayed = displayed.filter((c) => c.owner_id === addedBy);
        }
    }
    // V2.0.24 — boosted count comes from /browse counts.boosted (workspace-wide),
    // not client-side scan of the current page.
    const boostedCount = priorityStats?.boosted || 0;

    // V2.0.12 — separate counts:
    //   advancedFilterCount → filters that live inside the Advanced drawer
    //   activeFilterCount   → total actives (drives Clear Filters visibility)
    const advancedFilterCount = [
        skills.length ? "1" : "", location, minYrs, maxYrs, minSalary, maxSalary,
        stage, source, tags.length ? "1" : "", jobId, clientId, addedWithin,
        addedBy,
    ].filter((v) => v !== "" && v != null).length;
    const activeFilterCount = advancedFilterCount + [
        q.trim(), priority, origin, verificationTier,
    ].filter((v) => v !== "" && v != null).length;

    const resetAll = () => {
        setQ(""); setStage(""); setBoostedOnly(false);
        setSkills([]); setLocation(""); setMinYrs(""); setMaxYrs("");
        setMinSalary(""); setMaxSalary(""); setSource(""); setTags([]);
        setJobId(""); setClientId(""); setAddedWithin("");
        setAddedBy(""); setSort("recent");
        // V2.0.33 — pending* setters removed with the chip rows.
        setPriority(""); setOrigin(""); setVerificationTier("");
    };

    const jobMap = useMemo(() => jobs.reduce((acc, j) => ({ ...acc, [j.id]: j }), {}), [jobs]);

    // ---- Bulk selection helpers ----
    const toggleOne = (id) => {
        const next = new Set(selectedIds);
        if (next.has(id)) next.delete(id); else next.add(id);
        setSelectedIds(next);
        if (selectAllMatching) setSelectAllMatching(false);
    };
    const clearSelection = () => {
        setSelectedIds(new Set());
        setSelectAllMatching(false);
    };
    const allVisibleSelected = displayed.length > 0 && displayed.every((c) => selectedIds.has(c.id));
    const toggleAllVisible = () => {
        if (allVisibleSelected) {
            const next = new Set(selectedIds);
            displayed.forEach((c) => next.delete(c.id));
            setSelectedIds(next);
            setSelectAllMatching(false);
        } else {
            const next = new Set(selectedIds);
            displayed.forEach((c) => next.add(c.id));
            setSelectedIds(next);
        }
    };
    const selectAllAcrossPages = async () => {
        try {
            const r = await api.get("/candidates/search", { params: { ...params, ids_only: 1, limit: 5000 } });
            setSelectedIds(new Set(r.data?.ids || []));
            setSelectAllMatching(true);
            toast.success(`Selected all ${r.data?.ids?.length || 0} candidates matching your filters.`);
        } catch (e) {
            logError("select-all failed", e);
            toast.error("Couldn't select all matching candidates.");
        }
    };

    // ---- Bulk actions ----
    const applyBulkTag = async (tags) => {
        setBulkBusy(true);
        const candidateIds = Array.from(selectedIds);
        try {
            const r = await api.post("/candidates/bulk/tag", { candidate_ids: candidateIds, tags });
            const d = r.data || {};
            const undoIds = d.allowed_ids || candidateIds;
            toast.success(
                `Tagged ${d.matched || 0} candidate${d.matched === 1 ? "" : "s"}${d.skipped ? ` (${d.skipped} skipped — no access)` : ""}.`,
                {
                    duration: 6000,
                    action: {
                        label: "Undo",
                        onClick: async () => {
                            try {
                                await api.post("/candidates/bulk/untag", { candidate_ids: undoIds, tags });
                                toast("Tag removed.");
                                load();
                                api.get("/candidates/search/facets").then((rr) => setFacets(rr.data || facets)).catch(() => {});
                            } catch (ex) {
                                toast.error(ex?.response?.data?.detail || "Undo failed.");
                            }
                        },
                    },
                },
            );
            clearSelection();
            load();
            // Refresh facets so newly created tags appear in the filter chips list.
            api.get("/candidates/search/facets").then((rr) => setFacets(rr.data || facets)).catch(() => {});
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Bulk tag failed.");
        } finally {
            setBulkBusy(false);
        }
    };

    const applyBulkCopy = async (targetJobId) => {
        setBulkBusy(true);
        try {
            const r = await api.post("/candidates/bulk/copy", { candidate_ids: Array.from(selectedIds), target_job_id: targetJobId });
            const d = r.data || {};
            const msg = `Copied ${d.created || 0} to “${d.target_job_title || "requirement"}”${d.skipped ? ` · ${d.skipped} duplicate${d.skipped === 1 ? "" : "s"} skipped` : ""}.`;
            if (d.created > 0) toast.success(msg); else toast.info(msg);
            clearSelection();
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Bulk copy failed.");
        } finally {
            setBulkBusy(false);
        }
    };

    const applyBulkExport = async () => {
        setBulkBusy(true);
        try {
            const r = await api.post(
                "/candidates/bulk/export",
                { candidate_ids: Array.from(selectedIds) },
                { responseType: "blob" },
            );
            const blob = new Blob([r.data], { type: "text/csv" });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `candidates_${new Date().toISOString().slice(0, 10)}.csv`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success(`Exported ${selectedIds.size} candidate${selectedIds.size === 1 ? "" : "s"}.`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Export failed.");
        } finally {
            setBulkBusy(false);
        }
    };

    // V1.4 — Bulk copy sign-in links. Mints/reuses one magic URL per
    // selected candidate and copies a formatted block to clipboard so the
    // recruiter can paste into WhatsApp / SMS in one shot. Fallback to a
    // hidden textarea for non-secure contexts.
    const [bulkLinkFormat, setBulkLinkFormat] = useState("plain"); // "plain" | "whatsapp"
    const applyBulkCopyLinks = async () => {
        setBulkBusy(true);
        try {
            const { data } = await api.post("/candidates/bulk/magic-links", {
                candidate_ids: Array.from(selectedIds),
                format: bulkLinkFormat,
            });
            const text = data?.combined_text || "";
            if (!text) {
                toast.info(
                    `No links generated — none of the selected candidates have an email on file.`,
                );
                return;
            }
            let copied = false;
            try {
                await navigator.clipboard.writeText(text);
                copied = true;
            } catch {
                const ta = document.createElement("textarea");
                ta.value = text;
                ta.style.position = "fixed";
                ta.style.opacity = "0";
                document.body.appendChild(ta);
                ta.select();
                try { copied = document.execCommand("copy"); } catch { /* noop */ }
                document.body.removeChild(ta);
            }
            const skipped = data?.skipped?.length || 0;
            if (copied) {
                toast.success(
                    `${data.count} link${data.count === 1 ? "" : "s"} copied` +
                    (skipped ? ` · ${skipped} skipped (no email)` : "") + ".",
                    { duration: 5000 },
                );
            } else {
                toast.info(text, { duration: 12000 });
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Bulk copy links failed.");
        } finally {
            setBulkBusy(false);
        }
    };

    return (
        <div className={`px-3 sm:px-8 py-4 sm:py-10 max-w-[1600px] w-full min-w-0 mx-auto ${selectedIds.size > 0 ? "pb-24" : ""}`} data-testid="candidates-page" style={{ maxWidth: "min(1600px, 100vw)" }}>
            <div className="mb-5 sm:mb-10 flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <p className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-1 sm:mb-2">
                        Candidate Search
                    </p>
                    <h1 className="font-heading text-3xl sm:text-5xl font-black tracking-tighter text-ink">
                        Candidates.
                    </h1>
                </div>
                <button
                    type="button"
                    data-testid="candidates-import-btn"
                    onClick={() => setShowImportModal(true)}
                    className="mt-1 shrink-0 inline-flex items-center gap-2 h-10 px-3 sm:px-4 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-xs sm:text-sm font-black uppercase tracking-wider shadow-sm"
                >
                    <Upload size={16} weight="bold" />
                    <span className="hidden sm:inline">Import CSV / Excel</span>
                    <span className="sm:hidden">Import</span>
                </button>
            </div>

            {/* --- Top row: text search + advanced toggle + sort --- */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mb-4">
                <div className="relative flex-1">
                    <MagnifyingGlass
                        size={16}
                        weight="bold"
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]"
                    />
                    <input
                        data-testid="candidate-search-input"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search name, email, title, resume text…"
                        className="w-full pl-10 pr-4 py-2.5 bg-white border border-[#E5E5E5] focus:outline-none focus:border-cobalt focus:ring-1 focus:ring-cobalt text-sm"
                    />
                </div>
                <div className="flex items-center gap-2">
                    <button
                        data-testid="candidate-search-advanced-toggle"
                        onClick={() => setShowAdvanced((v) => !v)}
                        className={`flex-1 sm:flex-initial px-3 py-2 text-[10px] font-black uppercase tracking-[0.2em] transition-colors inline-flex items-center justify-center gap-1.5 ${
                            showAdvanced || advancedFilterCount > 0
                                ? "bg-ink text-white border border-ink"
                                : "border border-[#E5E5E5] text-[#525252] hover:border-ink"
                        }`}
                    >
                        <FunnelSimple size={12} weight="bold" />
                        Advanced
                        {advancedFilterCount > 0 && (
                            <span className={`ml-1 px-1.5 py-0.5 text-[9px] font-black ${showAdvanced || advancedFilterCount > 0 ? "bg-white/25" : "bg-[#F5F5F5]"}`}>
                                {advancedFilterCount}
                            </span>
                        )}
                    </button>
                    <BareSelect
                        value={sort}
                        onChange={setSort}
                        options={SORTS}
                        testId="candidate-search-sort"
                        ariaLabel="Sort"
                        className="flex-1 sm:flex-initial"
                    />
                    {canSeeAllOwners && (
                        <select
                            data-testid="candidate-search-added-by"
                            value={addedBy}
                            onChange={(e) => setAddedBy(e.target.value)}
                            aria-label="Added by"
                            className="flex-1 sm:flex-initial min-w-0 sm:min-w-[160px] bg-white border border-[#E5E5E5] px-3 py-2 text-xs font-bold uppercase tracking-wider text-[#525252] hover:border-ink focus:outline-none focus:border-cobalt"
                        >
                            <option value="">Added by: all</option>
                            <option value="__me__">Added by: me</option>
                            <option value="__pool__">Added by: team pool</option>
                            <option disabled>──────────</option>
                            {teammates.map((t) => (
                                <option key={t.id} value={t.id}>
                                    {t.name || t.email}
                                </option>
                            ))}
                        </select>
                    )}
                </div>
            </div>

            {/* --- V2.0.33 Stage chip row (replaces Priority + Type + Signals
                chip rows — see comment on STAGE_CHIP_META above). Recruiter
                triage is now stage-first: All / Applied / Screening /
                Interview / Offer / Hired / Rejected — with live counts
                from /candidates/browse.counts.stage. --- */}
            <StageChipsRow
                stage={stage}
                setStage={setStage}
                stageCounts={stageCounts}
                total={priorityStats?.total || 0}
            />
            {activeFilterCount > 0 && (
                <div className="mb-4 flex justify-end">
                    <button
                        data-testid="candidate-search-clear-all"
                        onClick={resetAll}
                        className="text-xs font-bold uppercase tracking-wider text-[#DC2626] hover:underline inline-flex items-center gap-1"
                    >
                        <X size={12} weight="bold" /> Clear filters
                    </button>
                </div>
            )}

            {/* V1.5.4 — Bulk nudge stale CVs. Recruiter/admin-only. */}
            <div className="mb-4 flex justify-end">
                <button
                    data-testid="candidates-stale-cv-nudge-btn"
                    onClick={() => setShowStaleCvNudge(true)}
                    title="Send a personalised 'Refresh your CV' email to candidates whose CV is 90+ days old (or missing)"
                    className="text-[10px] font-black uppercase tracking-wider text-amber-800 border border-amber-300 bg-amber-50 hover:bg-amber-100 px-3 py-1.5 inline-flex items-center gap-1.5 transition-colors"
                >
                    <Bell size={11} weight="fill" />
                    Nudge stale CVs
                </button>
            </div>

            {/* --- Advanced panel (collapsible) --- */}
            {showAdvanced && (
                <AdvancedPanel
                    skills={skills} setSkills={setSkills}
                    location={location} setLocation={setLocation}
                    minYrs={minYrs} setMinYrs={setMinYrs}
                    maxYrs={maxYrs} setMaxYrs={setMaxYrs}
                    minSalary={minSalary} setMinSalary={setMinSalary}
                    maxSalary={maxSalary} setMaxSalary={setMaxSalary}
                    source={source} setSource={setSource}
                    tags={tags} setTags={setTags}
                    jobId={jobId} setJobId={setJobId}
                    clientId={clientId} setClientId={setClientId}
                    addedWithin={addedWithin} setAddedWithin={setAddedWithin}
                    facets={facets}
                    jobs={jobs}
                    clients={clients}
                />
            )}

            {/* --- Result count strip --- */}
            <div
                data-testid="candidate-search-count"
                className="flex flex-wrap items-center justify-between gap-2 mb-3 text-xs text-[#525252]"
            >
                <span>
                    {loading ? "Searching…" : (
                        <>
                            Showing <span className="font-black text-ink">{displayed.length}</span> of{" "}
                            <span className="font-black text-ink">{total}</span> candidate{total === 1 ? "" : "s"}
                        </>
                    )}
                </span>
                {/* V2.0.3 — mobile-only row density toggle (comfortable / compact) */}
                <div
                    data-testid="row-density-toggle"
                    className="inline-flex items-center rounded-full bg-black/5 p-0.5 text-[10px] font-black uppercase tracking-wider"
                >
                    <button
                        data-testid="row-density-comfortable"
                        type="button"
                        onClick={() => setRowDensity("comfortable")}
                        aria-pressed={rowDensity === "comfortable"}
                        className={`inline-flex items-center gap-1 px-2 h-6 rounded-full transition-colors ${rowDensity === "comfortable" ? "bg-white shadow text-ink" : "text-ink/50"}`}
                    >
                        <Rows size={11} weight="bold" />
                        Comfy
                    </button>
                    <button
                        data-testid="row-density-compact"
                        type="button"
                        onClick={() => setRowDensity("compact")}
                        aria-pressed={rowDensity === "compact"}
                        className={`inline-flex items-center gap-1 px-2 h-6 rounded-full transition-colors ${rowDensity === "compact" ? "bg-white shadow text-ink" : "text-ink/50"}`}
                    >
                        <ListIcon size={11} weight="bold" />
                        Compact
                    </button>
                </div>
                {allVisibleSelected && !selectAllMatching && total > displayed.length && (
                    <button
                        data-testid="bulk-select-all-matching"
                        onClick={selectAllAcrossPages}
                        className="text-xs font-black uppercase tracking-wider text-cobalt hover:underline"
                    >
                        Select all {total} matching filters
                    </button>
                )}
                {selectAllMatching && (
                    <span data-testid="bulk-select-all-matching-on" className="text-xs font-black uppercase tracking-wider text-cobalt">
                        All {selectedIds.size} matching candidates selected
                    </span>
                )}
            </div>

            {/* --- Results table --- */}
            <div className="bg-white border border-[#E5E5E5]" data-density={rowDensity === "compact" ? "compact" : "cozy"} data-testid="candidates-list-container">
                <div className="hidden sm:grid grid-cols-12 gap-4 px-6 py-3 border-b border-[#E5E5E5] text-[10px] font-black uppercase tracking-wider text-[#A3A3A3] items-center">
                    <div className="col-span-4 flex items-center gap-3">
                        <button
                            type="button"
                            data-testid="bulk-select-all-visible"
                            onClick={toggleAllVisible}
                            aria-label={allVisibleSelected ? "Deselect all visible" : "Select all visible"}
                            className="text-[#525252] hover:text-ink shrink-0"
                        >
                            {allVisibleSelected ? (
                                <CheckSquare size={18} weight="fill" className="text-cobalt" />
                            ) : (
                                <Square size={18} weight="bold" />
                            )}
                        </button>
                        Candidate
                    </div>
                    <div className="col-span-3">Requirement</div>
                    <div className="col-span-2">Stage</div>
                    <div className="col-span-2">Match</div>
                    <div className="col-span-1 text-right">Exp.</div>
                </div>
                {!loading && displayed.length === 0 && (
                    <div className="p-10 text-center" data-testid="no-candidates-empty">
                        <p className="text-[#525252] mb-2">No candidates match your filters.</p>
                        {activeFilterCount > 0 && (
                            <button
                                onClick={resetAll}
                                className="text-xs font-black uppercase tracking-wider text-cobalt hover:underline"
                            >
                                Clear filters
                            </button>
                        )}
                    </div>
                )}
                {displayed.map((c) => (
                    <div
                        key={c.id}
                        data-testid={`candidate-row-${c.id}`}
                        data-role="candidate-row"
                        role="button"
                        tabIndex={0}
                        onClick={() => setPersonCid(c.id)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                setPersonCid(c.id);
                            }
                        }}
                        className={`w-full px-3 sm:px-6 py-3 sm:py-4 border-b border-[#E5E5E5] hover:bg-[#F9F9F9] transition-colors text-left cursor-pointer relative ${
                            c.is_boosted ? "sm:bg-gradient-to-r sm:from-[#FFFBEB] sm:via-white sm:to-white border-l-2 border-l-amber-400 sm:border-l-0" : ""
                        }`}
                    >
                        {/* Desktop row */}
                        <div className="hidden sm:grid grid-cols-12 gap-4 items-center">
                            <div className="col-span-4 min-w-0 flex items-start gap-3">
                                <button
                                    type="button"
                                    data-testid={`bulk-select-${c.id}`}
                                    onClick={(e) => { e.stopPropagation(); toggleOne(c.id); }}
                                    aria-label={selectedIds.has(c.id) ? "Deselect candidate" : "Select candidate"}
                                    className="mt-0.5 shrink-0 text-[#525252] hover:text-ink"
                                >
                                    {selectedIds.has(c.id) ? (
                                        <CheckSquare size={18} weight="fill" className="text-cobalt" />
                                    ) : (
                                        <Square size={18} weight="bold" />
                                    )}
                                </button>
                                {showAvatars && (
                                    <RowAvatar
                                        url={c.avatar_url}
                                        name={c.name}
                                        size={36}
                                        testid={`row-avatar-${c.id}`}
                                    />
                                )}
                                <div className="min-w-0 flex-1">
                                    <p className="font-bold text-ink truncate flex items-center gap-2">
                                        {c.name}
                                        {c.is_boosted && <FeaturedBadge />}
                                        <VerifiedTierChip tier={c.verification_tier} />
                                        <OriginChip origin={c.origin} source={c.source} testId={`origin-chip-${c.id}`} />
                                        {(c.posting_count || 1) > 1 && (
                                            <span
                                                data-testid={`multi-posting-badge-${c.id}`}
                                                title={`This candidate is on ${c.posting_count} requirements`}
                                                className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wider bg-cobalt/10 text-cobalt px-1.5 py-0.5"
                                            >
                                                {c.posting_count} reqs
                                            </span>
                                        )}
                                        <DesktopPriorityPill priority={c.priority} testid={`desktop-priority-pill-${c.id}`} />
                                    </p>
                                    <p className="text-xs text-[#525252] truncate">
                                        {c.current_title || "—"} · {c.email}
                                    </p>
                                    <div className="mt-1 flex items-center gap-2 flex-wrap">
                                        {c.code && (
                                            <span
                                                data-testid={`list-candidate-code-${c.id}`}
                                                className="font-mono text-[9px] tracking-wider text-[#A3A3A3]"
                                            >
                                                {c.code}
                                            </span>
                                        )}
                                        <SourceBadge source={c.source} />
                                        <CvFreshnessBadge candidate={c} />
                                        <CvActions candidate={c} compact />
                                        {c.work_mode_preference && c.work_mode_preference !== "Any" && (
                                            <span
                                                data-testid={`list-candidate-wm-${c.id}`}
                                                className={`text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 border ${
                                                    c.work_mode_preference === "Remote"
                                                        ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                                                        : c.work_mode_preference === "Hybrid"
                                                            ? "bg-violet-50 text-violet-700 border-violet-100"
                                                            : "bg-slate-50 text-slate-700 border-slate-100"
                                                }`}
                                                title={`Preferred work mode: ${c.work_mode_preference}`}
                                            >
                                                {c.work_mode_preference}
                                            </span>
                                        )}
                                        {(c.tags || []).slice(0, 3).map((t) => (
                                            <span key={t} className="text-[9px] font-black uppercase tracking-wider text-[#525252] bg-[#F5F5F5] px-1.5 py-0.5">
                                                {t}
                                            </span>
                                        ))}
                                        {/* V2.0.46 — chat activity signal (desktop).
                                            Lives in the candidate chip row (not the
                                            truncated Requirement column) so `truncate`
                                            can't clip it. */}
                                        {c.last_chat_at && (() => {
                                            const rel = _relTime(c.last_chat_at);
                                            return rel ? (
                                                <span
                                                    data-testid={`desktop-lastchat-chip-${c.id}`}
                                                    title={`Last chat activity ${rel}`}
                                                    className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wider text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded-sm"
                                                >
                                                    <ChatCircleDots size={10} weight="bold" /> Msg'd {rel}
                                                </span>
                                            ) : null;
                                        })()}
                                    </div>
                                </div>
                            </div>
                            <div className="col-span-3 text-sm text-[#525252] truncate">
                                {c.job_title || jobMap[c.job_id]?.title || "—"}
                                {c.client_name && (
                                    <span className="block text-[10px] text-[#A3A3A3] truncate">{c.client_name}</span>
                                )}
                                {(c.posting_count || 1) > 1 && (
                                    <span className="block text-[10px] text-cobalt font-black uppercase tracking-wider mt-0.5">
                                        + {c.posting_count - 1} more · view in ⋮
                                    </span>
                                )}
                                {(canSeeAllOwners || c.is_team_pool || c.owner_id === user?.id) && (
                                    <span className="inline-block mt-1">
                                        <AddedByChip
                                            name={c.added_by_name}
                                            isTeamPool={!!c.is_team_pool}
                                            isMine={!!c.owner_id && c.owner_id === user?.id}
                                            size="sm"
                                        />
                                    </span>
                                )}
                            </div>
                            <div className="col-span-2">
                                <StageChip stage={c.stage} />
                            </div>
                            <div className="col-span-2">
                                {c.match_score != null ? (
                                    <span className="inline-flex items-center gap-1 text-sm font-bold text-ink">
                                        <Sparkle size={12} weight="fill" className="text-cobalt" />
                                        {c.match_score}%
                                    </span>
                                ) : (
                                    <span className="text-xs text-[#A3A3A3]">—</span>
                                )}
                            </div>
                            <div className="col-span-1 text-right text-sm text-[#525252]">
                                {c.years_experience}y
                            </div>
                        </div>
                        {/* Kebab menu — top-right on desktop, top-right (small) on mobile */}
                        <div
                            className="absolute top-1.5 right-1.5 sm:top-4 sm:right-4 z-10"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <RowKebab
                                candidate={c}
                                onDone={load}
                                onOpenCandidate={(cid) => setSelectedId(cid)}
                                onOpen360={(cid) => setPersonCid(cid)}
                            />
                        </div>
                        {/* Mobile row */}
                        <div className="sm:hidden pr-8">
                            {rowDensity === "comfortable" ? (
                                <MobileCandidateRow
                                    candidate={c}
                                    jobMap={jobMap}
                                    selected={selectedIds.has(c.id)}
                                    onToggleSelect={(e) => { e.stopPropagation(); toggleOne(c.id); }}
                                    showAvatars={showAvatars}
                                />
                            ) : (
                                <div className="flex flex-col gap-2">
                                    <div className="flex items-start gap-2 min-w-0">
                                        <button
                                            type="button"
                                            data-testid={`bulk-select-mobile-${c.id}`}
                                            onClick={(e) => { e.stopPropagation(); toggleOne(c.id); }}
                                            aria-label={selectedIds.has(c.id) ? "Deselect candidate" : "Select candidate"}
                                            className="mt-0.5 shrink-0 text-[#525252]"
                                        >
                                            {selectedIds.has(c.id) ? (
                                                <CheckSquare size={18} weight="fill" className="text-cobalt" />
                                            ) : (
                                                <Square size={18} weight="bold" />
                                            )}
                                        </button>
                                        {showAvatars && (
                                            <RowAvatar
                                                url={c.avatar_url}
                                                name={c.name}
                                                size={32}
                                                testid={`row-avatar-mobile-${c.id}`}
                                                isOnline={!!c.is_online}
                                            />
                                        )}
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                <p className="font-bold text-ink truncate">{c.name}</p>
                                                {c.is_boosted && <FeaturedBadge compact />}
                                                <VerifiedTierChip tier={c.verification_tier} size="xs" />
                                                {(c.posting_count || 1) > 1 && (
                                                    <span className="text-[9px] font-black uppercase tracking-wider bg-cobalt/10 text-cobalt px-1.5 py-0.5">
                                                        {c.posting_count} reqs
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-xs text-[#525252] truncate mt-0.5">
                                                {c.current_title || "—"}
                                            </p>
                                            <p className="text-[10px] text-[#A3A3A3] truncate">
                                                {c.job_title || jobMap[c.job_id]?.title || "—"}
                                                {(c.posting_count || 1) > 1 && ` + ${c.posting_count - 1} more`}
                                            </p>
                                        </div>
                                        <div className="flex flex-col items-end gap-1 shrink-0">
                                            <StageChip stage={c.stage} />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {selectedIds.size > 0 && (
                <BulkActionBar
                    count={selectedIds.size}
                    busy={bulkBusy}
                    onTag={() => setShowTagModal(true)}
                    onCopy={() => setShowCopyModal(true)}
                    onExport={applyBulkExport}
                    onReachOut={() => setShowBulkReachOut(true)}
                    onCopyLinks={applyBulkCopyLinks}
                    linkFormat={bulkLinkFormat}
                    onLinkFormatChange={setBulkLinkFormat}
                    onClear={clearSelection}
                />
            )}

            <BulkReachOutModal
                open={showBulkReachOut}
                candidateIds={showBulkReachOut ? Array.from(selectedIds) : []}
                onClose={() => setShowBulkReachOut(false)}
            />

            <BulkStaleCvNudgeModal
                open={showStaleCvNudge}
                onClose={() => setShowStaleCvNudge(false)}
            />

            <CandidateImportModal
                open={showImportModal}
                onClose={() => setShowImportModal(false)}
                onDone={load}
            />

            <TagBulkModal
                open={showTagModal}
                onOpenChange={setShowTagModal}
                count={selectedIds.size}
                existingTags={facets.tags || []}
                onSubmit={applyBulkTag}
            />

            <CopyBulkModal
                open={showCopyModal}
                onOpenChange={setShowCopyModal}
                count={selectedIds.size}
                jobs={jobs}
                onSubmit={applyBulkCopy}
            />

            {selectedId && (
                <CandidateDrawer
                    candidateId={selectedId}
                    onClose={() => setSelectedId(null)}
                    onUpdated={load}
                    onOpenCandidate={(other) => setSelectedId(other)}
                />
            )}

            <PersonDetailPanel
                open={!!personCid}
                cid={personCid}
                jobs={jobs}
                onClose={() => setPersonCid(null)}
                onOpenPosting={(cid) => { setPersonCid(null); setSelectedId(cid); }}
                onCandidateChange={async (changedCid, freshCandidate = null) => {
                    // V1.5.8.13 (Feb 17, 2026) — accept the fresh candidate
                    // from the upload response directly. Fixes the race where
                    // the follow-up GET occasionally returned a stale row and
                    // the "NO CV" chip stayed sticky (prod bug reported Feb 17).
                    // If no fresh candidate provided, fall back to a refetch.
                    try {
                        let data = freshCandidate;
                        if (!data) {
                            const resp = await api.get(`/candidates/${changedCid}`);
                            data = resp.data;
                        }
                        setItems((prev) =>
                            prev.map((c) => (c.id === changedCid ? { ...c, ...data } : c))
                        );
                    } catch {
                        // Soft-fail — worst case the user closes the drawer
                        // and re-opens for the fresh chip.
                    }
                }}
            />
        </div>
    );
}
